import React, { useState } from 'react';
import {
  FiArrowRight,
  FiMail,
  FiPhone,
  FiMapPin,
  FiUser,
  FiBriefcase,
  FiCheck,
  FiMessageSquare,
  FiUsers,
  FiFileText,
  FiClock
} from 'react-icons/fi';
import {
  FaLinkedin,
  FaTwitter,
  FaWhatsapp
} from 'react-icons/fa';
import styled from 'styled-components';
import Button from '../../components/shared/Button';
import { Link } from 'react-router-dom';
import axios from 'axios'; // Importando axios que estava faltando

// Animations
const fadeIn = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

// Design Tokens
const colors = {
  primary: '#151c48',
  primaryLight: '#2a3a8f',
  accent: '#5ab9d3',
  white: '#ffffff',
  lightBg: '#f8f9ff',
  textPrimary: '#1a1a1a',
  textSecondary: '#5f6c84'
};

const spacing = {
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px',
  full: '50px'
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px'
};

// Styled Components
const PremiumContactContainer = styled.div`
  width: 100%;
  background: ${colors.lightBg};
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryLight} 100%);
  color: ${colors.white};
  padding: 120px 20px;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('https://images.unsplash.com/photo-1556905055-8f358a7a47b2?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80') center/cover;
    opacity: 0.15;
    z-index: 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: ${spacing.lg};
  line-height: 1.2;
  letter-spacing: -1px;
  animation: ${fadeIn} 0.8s ease-out;
  
  span {
    color: ${colors.accent};
  }

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  max-width: 700px;
  margin: 0 auto ${spacing.xxl};
  opacity: 0.9;
  animation: ${fadeIn} 0.8s ease-out 0.2s forwards;
  opacity: 0;

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${spacing.xxl};
  margin: ${spacing.xxxl} auto;
  max-width: 1200px;
  padding: 0 ${spacing.xl};

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.03);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.accent});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${spacing.lg};
  color: ${colors.white};
  font-size: 1.8rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.md};
`;

const FeatureText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.6;
`;

const ContactSection = styled.section`
  padding: ${spacing.xxxl} 0;
  background: ${colors.white};
`;

const ContactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.xxl};
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContactCard = styled.div`
  background: ${colors.lightBg};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.03);
`;

const ContactTitle = styled.h2`
  font-size: 1.8rem;
  color: ${colors.primary};
  margin-bottom: ${spacing.lg};
  display: flex;
  align-items: center;
  
  svg {
    margin-right: ${spacing.md};
    color: ${colors.accent};
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: ${spacing.lg};
  
  svg {
    color: ${colors.accent};
    margin-right: ${spacing.md};
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const ContactText = styled.p`
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: ${spacing.md};
  margin-top: ${spacing.xxl};
`;

const SocialButton = styled.button`
  color: ${colors.primary};
  background: ${colors.white};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  padding: 0;
  
  &:hover {
    transform: translateY(-3px);
    color: ${colors.white};
    background: ${colors.primary};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(90, 185, 211, 0.3);
  }
`;

const FormGroup = styled.div`
  margin-bottom: ${spacing.lg};
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${spacing.sm};
  color: ${colors.primary};
  font-weight: 500;
`;

const FormInput = styled.input`
  width: 100%;
  padding: ${spacing.md};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${borderRadius.md};
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px rgba(90, 185, 211, 0.2);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: ${spacing.md};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${borderRadius.md};
  font-size: 1rem;
  transition: all 0.3s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right ${spacing.md} center;
  background-size: 1em;
  
  &:focus {
    outline: none;
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px rgba(90, 185, 211, 0.2);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  padding: ${spacing.md};
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: ${borderRadius.md};
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${colors.accent};
    box-shadow: 0 0 0 3px rgba(90, 185, 211, 0.2);
  }
`;

const SuccessMessage = styled.div`
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  margin-top: ${spacing.lg};
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    margin-right: ${spacing.sm};
  }
`;

const CenteredButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${spacing.xl};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${colors.white};
  padding: ${spacing.xxl};
  border-radius: ${borderRadius.xl};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const ModalTitle = styled.h3`
  color: ${props => props.success ? '#10b981' : '#ef4444'};
  margin-bottom: ${spacing.md};
`;

const ModalButton = styled.button`
  background: ${colors.primary};
  color: ${colors.white};
  border: none;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${borderRadius.md};
  margin-top: ${spacing.lg};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.primaryLight};
  }
`;

const PremiumFeatures = [
  {
    icon: <FiUsers size={28} />,
    title: "Conexão com Especialistas",
    text: "Acesso direto a empresas e profissionais qualificados para sua realocação"
  },
  {
    icon: <FiFileText size={28} />,
    title: "Gestão de Processos",
    text: "Acompanhamento centralizado de toda a documentação necessária"
  },
  {
    icon: <FiClock size={28} />,
    title: "Agilidade",
    text: "Respostas rápidas das empresas cadastradas em nossa plataforma"
  }
];

const PremiumContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [contactPurpose, setContactPurpose] = useState('assistance');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [modalSuccess, setModalSuccess] = useState(false);

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Por favor, insira seu nome');
      return false;
    }

    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Por favor, insira um email válido');
      return false;
    }

    if (!formData.phone.trim()) {
      setError('Por favor, insira seu telefone');
      return false;
    }

    if (contactPurpose === 'partnership' && !formData.company.trim()) {
      setError('Por favor, insira o nome da empresa');
      return false;
    }

    if (!formData.message.trim()) {
      setError('Por favor, insira sua mensagem');
      return false;
    }

    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePurposeChange = (e) => {
    setContactPurpose(e.target.value);
    // Reset company field when changing purpose
    if (e.target.value !== 'partnership') {
      setFormData(prev => ({ ...prev, company: '' }));
    }
  };

   const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setModalMessage(error || 'Por favor, preencha todos os campos obrigatórios');
      setModalSuccess(false);
      setModalOpen(true);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        purpose: contactPurpose.toUpperCase(),
        message: formData.message
      };

      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/contacts`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      setModalMessage(getSuccessMessage());
      setModalSuccess(true);
      setModalOpen(true);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });

    } catch (err) {
      console.error('Erro ao enviar contato:', err);

      let errorMessage = 'Erro ao enviar sua mensagem. Por favor, tente novamente.';

      if (err.response) {
        const status = err.response.status;

        if (status === 400) {
          errorMessage = err.response.data?.message || 'Dados inválidos. Verifique os campos.';
        } else if (status === 401) {
          errorMessage = 'Autenticação necessária. Por favor, faça login.';
        } else if (status === 403) {
          errorMessage = 'Você não tem permissão para realizar esta ação.';
        } else if (status >= 500) {
          errorMessage = 'Erro interno no servidor. Tente novamente mais tarde.';
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        }

      } else if (err.request) {
        errorMessage = 'Servidor indisponível no momento. Tente novamente mais tarde.';
      }

      setModalMessage(errorMessage);
      setModalSuccess(false);
      setModalOpen(true);

    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
  };


  const getMessagePlaceholder = () => {
    switch (contactPurpose) {
      case 'assistance':
        return "Descreva seu tipo de visto, necessidades de moradia, prazo para realocação, etc.";
      case 'partnership':
        return "Descreva sua proposta de parceria e como podemos trabalhar juntos";
      case 'feedback':
        return "Seu feedback é muito importante para nós. Conte o que achou da plataforma!";
      default:
        return "Como podemos ajudar?";
    }
  };

  const getSuccessMessage = () => {
    switch (contactPurpose) {
      case 'assistance':
        return "Sua solicitação foi enviada! Empresas cadastradas entrarão em contato em breve.";
      case 'partnership':
        return "Sua proposta de parceria foi enviada com sucesso! Nossa equipe entrará em contato em breve.";
      case 'feedback':
        return "Obrigado pelo seu feedback! Sua opinião é muito valiosa para nós.";
      default:
        return "Sua mensagem foi enviada com sucesso!";
    }
  };

  const getContactTitle = () => {
    switch (contactPurpose) {
      case 'assistance':
        return "Conecte-se com Especialistas";
      case 'partnership':
        return "Fale sobre Parcerias";
      case 'feedback':
        return "Envie seu Feedback";
      default:
        return "Entre em Contato";
    }
  };

  return (
    <PremiumContactContainer>
      <HeroSection>
        <Container>
          <HeroTitle>
            <span>Conectamos você aos melhores serviços de realocação em Portugal</span>
          </HeroTitle>
          <HeroSubtitle>
            Nossa plataforma facilita a conexão entre quem busca realocação e empresas especializadas no processo
          </HeroSubtitle>
        </Container>
      </HeroSection>

      <Container>
        <FeaturesGrid>
          {PremiumFeatures.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureText>{feature.text}</FeatureText>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>

      <ContactSection id="contact">
        <ContactGrid>
          <ContactCard>
            <ContactTitle>
              <FiUser size={24} />
              Informações de Contato
            </ContactTitle>

            <ContactItem>
              <FiMail size={20} />
              <ContactText>contato@realocafacil.com</ContactText>
            </ContactItem>

            <ContactItem>
              <FiPhone size={20} />
              <ContactText>+351 123 456 789 (WhatsApp)</ContactText>
            </ContactItem>

            <ContactItem>
              <FiMapPin size={20} />
              <ContactText>Av. da Liberdade, 100, Lisboa, Portugal</ContactText>
            </ContactItem>

            <ContactTitle style={{ marginTop: spacing.xxl }}>
              <FiBriefcase size={24} />
              Para Profissionais
            </ContactTitle>
            <ContactText style={{ marginBottom: spacing.lg }}>
              Você é uma empresa de realocação em Portugal? Cadastre-se em nossa plataforma para conectar-se com novos clientes.
            </ContactText>
            <Button
              as={Link}
              to="/sign-up"
              primary={true}
              endIcon={<FiArrowRight />}
            >
              Cadastre-se
            </Button>

            <SocialLinks>
              <SocialButton aria-label="LinkedIn">
                <FaLinkedin size={18} />
              </SocialButton>
              <SocialButton aria-label="Twitter">
                <FaTwitter size={18} />
              </SocialButton>
              <SocialButton aria-label="WhatsApp">
                <FaWhatsapp size={18} />
              </SocialButton>
            </SocialLinks>
          </ContactCard>

          <ContactCard>
            <ContactTitle>
              <FiMessageSquare size={24} />
              {getContactTitle()}
            </ContactTitle>

            {isSubmitted ? (
              <SuccessMessage>
                <FiCheck size={20} />
                {getSuccessMessage()}
              </SuccessMessage>
            ) : (
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <FormLabel>Objetivo do Contato *</FormLabel>
                  <FormSelect
                    value={contactPurpose}
                    onChange={handlePurposeChange}
                  >
                    <option value="assistance">Assistência para realocação</option>
                    <option value="partnership">Propor parceria</option>
                    <option value="feedback">Enviar feedback/sugestão</option>
                  </FormSelect>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Nome Completo *</FormLabel>
                  <FormInput
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Email *</FormLabel>
                  <FormInput
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <FormLabel>Telefone *</FormLabel>
                  <FormInput
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>

                {contactPurpose === 'partnership' && (
                  <FormGroup>
                    <FormLabel>Nome da Empresa/Organização *</FormLabel>
                    <FormInput
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      required={contactPurpose === 'partnership'}
                    />
                  </FormGroup>
                )}

                <FormGroup>
                  <FormLabel>Mensagem *</FormLabel>
                  <FormTextarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder={getMessagePlaceholder()}
                  />
                </FormGroup>

                {error && (
                  <div style={{ color: '#ef4444', marginBottom: spacing.md }}>
                    {error}
                  </div>
                )}

                <CenteredButtonWrapper>
                  <Button
                    type="submit"
                    primary={true}
                    endIcon={<FiArrowRight />}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Enviando...' :
                      contactPurpose === 'assistance'
                        ? "Enviar Solicitação"
                        : contactPurpose === 'partnership'
                          ? "Enviar Proposta"
                          : "Enviar Feedback"}
                  </Button>
                </CenteredButtonWrapper>
              </form>
            )}
          </ContactCard>
        </ContactGrid>
      </ContactSection>

      {modalOpen && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle success={modalSuccess}>
              {modalSuccess ? 'Sucesso!' : 'Erro'}
            </ModalTitle>
            <p>{modalMessage}</p>
            <ModalButton onClick={closeModal}>
              Fechar
            </ModalButton>
          </ModalContent>
        </ModalOverlay>
      )}
    </PremiumContactContainer>
  );
};

export default PremiumContactPage;